import { createSelector } from 'reselect';
import type { AccountId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { AssigneeType, AvatarDetails, AvatarId } from '../../model/types';
import {
	assigneeTypeSelector,
	getAllProjectCategories,
	projectAvatarSelector,
	projectCategorySelector,
	projectDescriptionSelector,
	projectKeySelector,
	projectLeadAccountIdSelector,
	projectNameSelector,
	projectTeamTypeFreeTextSelector,
	projectTeamTypeSelector,
	projectUrlSelector,
} from '../entities/selectors';
import type { AppState } from '../types';
import type { Flag, UIState, ValidationErrors } from './types';

export const uiSelector = (state: AppState): UIState => state.ui;

export const projectKeyUiSelector = (state: AppState): string => uiSelector(state).projectKey;
export const historicalProjectKeysSelector = (state: AppState): string[] =>
	uiSelector(state).historicalProjectKeys;
export const releasedProjectKeysSelector = (state: AppState): string[] =>
	uiSelector(state).releasedProjectKeys;
export const projectNameUiSelector = (state: AppState): string => uiSelector(state).projectName;
export const getSimplifiedSelector = (state: AppState): boolean => uiSelector(state).simplified;
export const projectTeamTypeUiSelector = (state: AppState): string | undefined =>
	uiSelector(state).projectTeamType;
export const projectTeamTypeFreeTextUiSelector = (state: AppState): string | undefined =>
	uiSelector(state).projectTeamTypeFreeText;
export const projectDescriptionUiSelector = (state: AppState): string =>
	uiSelector(state).projectDescription;
export const projectUrlUiSelector = (state: AppState): string => uiSelector(state).projectUrl;

export const projectAvatarUiSelector = (state: AppState): AvatarDetails | undefined =>
	uiSelector(state).avatar;
export const projectCategoryIdUiSelector = (state: AppState): string | undefined =>
	uiSelector(state).projectCategoryId;
export const projectLeadAccountIdUiSelector = (state: AppState): AccountId =>
	uiSelector(state).projectLeadAccountId;
export const assigneeTypeUiSelector = (state: AppState): AssigneeType =>
	uiSelector(state).assigneeType;
export const projectAvatarIdUiSelector = (state: AppState): AvatarId | null => {
	const avatar = projectAvatarUiSelector(state);
	return avatar ? avatar.id : null;
};

export const flagsSelector = (state: AppState): Flag[] => uiSelector(state).flags;

export const validationSuccess: ValidationErrors = {
	projectName: undefined,
	projectKey: undefined,
	projectUrl: undefined,
	projectTeamTypeFreeText: undefined,
	releaseProjectKeysError: undefined,
};

export const validationErrorsSelector = (state: AppState): ValidationErrors =>
	uiSelector(state).validationErrors;

export const hasAvatarChanged = (state: AppState): boolean => {
	const projectAvatar: AvatarDetails | undefined = projectAvatarSelector(state);
	const uiAvatar: AvatarDetails | undefined = projectAvatarUiSelector(state);
	// We haven't loaded any data so we say that the avatar hasn't changed
	if (!projectAvatar || !uiAvatar) {
		return false;
	}
	return projectAvatar.id !== uiAvatar.id;
};

export const isUploadingAvatarSelector = (state: AppState) => state.ui.isUploadingAvatar;

export const projectNameMaxLength = (state: AppState): number | undefined =>
	uiSelector(state).projectNameMaxLength;
export const projectKeyMaxLength = (state: AppState): number | undefined =>
	uiSelector(state).projectKeyMaxLength;
export const allowUnassigned = (state: AppState): boolean | undefined =>
	uiSelector(state).allowUnassigned;
const projectKeyChanged = (state: AppState): boolean =>
	projectKeySelector(state) !== projectKeyUiSelector(state);
const releasedProjectKeysChanged = (state: AppState): boolean =>
	releasedProjectKeysSelector(state).length > 0;

const nameChanged = (state: AppState): boolean =>
	projectNameSelector(state) !== projectNameUiSelector(state);

const urlChanged = (state: AppState): boolean =>
	projectUrlSelector(state) !== projectUrlUiSelector(state);

const descriptionChanged = (state: AppState): boolean =>
	projectDescriptionSelector(state) !== projectDescriptionUiSelector(state);

const projectTeamTypeChanged = (state: AppState): boolean =>
	projectTeamTypeSelector(state) !== projectTeamTypeUiSelector(state);

const projectTeamTypeFreeTextChanged = (state: AppState): boolean =>
	projectTeamTypeFreeTextSelector(state) !== projectTeamTypeFreeTextUiSelector(state);

const projectCategoryChanged = (state: AppState): boolean => {
	const projectCategory = projectCategorySelector(state);
	const projectUiCategory = projectCategoryIdUiSelector(state);
	// Project category value -1 and undefined are both empty values and therefore same
	return (
		!(projectUiCategory === '-1' && projectCategory === undefined) &&
		projectCategory !== projectUiCategory
	);
};

export const projectLeadAccountIdChanged = (state: AppState): boolean =>
	projectLeadAccountIdSelector(state) !== projectLeadAccountIdUiSelector(state);

export const assigneeTypeChanged = (state: AppState): boolean =>
	assigneeTypeSelector(state) !== assigneeTypeUiSelector(state);

export const isFormDirtySelector = (state: AppState): boolean =>
	projectKeyChanged(state) ||
	nameChanged(state) ||
	urlChanged(state) ||
	descriptionChanged(state) ||
	projectCategoryChanged(state) ||
	hasAvatarChanged(state) ||
	projectLeadAccountIdChanged(state) ||
	assigneeTypeChanged(state) ||
	projectTeamTypeChanged(state) ||
	releasedProjectKeysChanged(state) ||
	projectTeamTypeFreeTextChanged(state);

export const isFormSubmittingSelector = (state: AppState): boolean => state.ui.isSubmitting;

export const forceValidationForSelector = (state: AppState): string | undefined =>
	uiSelector(state).forceValidationFor;

export const isSaveButtonEnabledSelector = (state: AppState): boolean => {
	const formIsDirty = isFormDirtySelector(state);
	const isSubmitting = isFormSubmittingSelector(state);
	return formIsDirty && !isSubmitting;
};

export const isAvatarPickerDialogOpenSelector = (state: AppState): boolean =>
	state.ui.isAvatarPickerDialogOpen;

export const isDeletingSelector = (state: AppState): boolean => state.ui.isDeleting;

export const isDeleteProjectModalOpenSelector = (state: AppState): boolean =>
	state.ui.isDeleteProjectModalOpen;

export const isTrashProjectModalOpenSelector = (state: AppState): boolean =>
	state.ui.isTrashProjectModalOpen;

export const isArchiveProjectModalOpenSelector = (state: AppState): boolean =>
	state.ui.isArchiveProjectModalOpen;

const isAvatarImageLoadedSelector = (state: AppState): boolean =>
	uiSelector(state).isAvatarImageLoaded;

export const isAvatarPickerEnabledSelector = (state: AppState): boolean =>
	isAvatarImageLoadedSelector(state) && !isUploadingAvatarSelector(state);

export const dropDownMenuButtonRefSelector = (state: AppState) =>
	uiSelector(state).dropDownMenuButtonRef;

export const projectKeyToSaveSelector = (state: AppState): string => {
	const projectKey = projectKeyUiSelector(state);
	// If key has changed we should trim it to make sure we're not saving unnecessary whitespace
	return projectKeyChanged(state) ? projectKey.trim() : projectKey;
};

export const projectNameToSaveSelector = (state: AppState): string => {
	const projectName = projectNameUiSelector(state);
	// If name has changed we should trim it to make sure we're not saving unnecessary whitespace
	return nameChanged(state) ? projectName.trim() : projectName;
};

export const projectUrlToSaveSelector = (state: AppState): string => {
	const projectUrl = projectUrlUiSelector(state);
	return urlChanged(state) ? projectUrl.trim() : projectUrl;
};

export const simplifiedToSaveSelector = (state: AppState): boolean => getSimplifiedSelector(state);

export const projectDescriptionToSaveSector = (state: AppState): string => {
	const projectDescription = projectDescriptionUiSelector(state);
	return descriptionChanged(state) ? projectDescription.trim() : projectDescription;
};

export const projectTeamTypeToSaveSector = (state: AppState): string | undefined => {
	const projectTeamType = projectTeamTypeUiSelector(state);
	return projectTeamTypeChanged(state) ? projectTeamType?.trim() : projectTeamType;
};

export const projectTeamTypeFreeTextToSaveSector = (state: AppState): string | undefined => {
	const projectTeamTypeFreeText = projectTeamTypeFreeTextUiSelector(state);
	return projectTeamTypeFreeTextChanged(state)
		? projectTeamTypeFreeText?.trim()
		: projectTeamTypeFreeText;
};
export const formUiValuesSelector = (state: AppState) => ({
	projectKey: projectKeyToSaveSelector(state),
	projectName: projectNameToSaveSelector(state),
	projectUrl: projectUrlToSaveSelector(state),
	projectDescription: projectDescriptionToSaveSector(state),
	projectIcon: projectAvatarUiSelector(state),
	projectLeadAccountId: projectLeadAccountIdUiSelector(state),
	assigneeType: assigneeTypeUiSelector(state),
	projectTeamType: projectTeamTypeToSaveSector(state),
	projectTeamTypeFreeText: projectTeamTypeFreeTextToSaveSector(state),
});

export const getProjectCategoryToDisplay = createSelector(
	[projectCategoryIdUiSelector, projectCategorySelector, getAllProjectCategories],
	(categoryidFromUi, projectCategoryId, allCategories) => {
		const categoryId = categoryidFromUi || projectCategoryId;
		return allCategories.find((category) => category.id === categoryId);
	},
);
